import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch } from '@/modules/shared/composables/use-fetch'
import { addItem, addItems, clearItems } from '@/modules/shared/utils/store'
import { CID } from '@/modules/shared/utils/store.types'
import { Money } from '@/modules/shared/utils/money'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import { useFetch as useFetchv4 } from '@/modules/core/composables/useFetch'
import { downloadAndSave } from '@/modules/filing/utils/utils'
///////////////////////////////////////////////////////////////////////////////
// Types
///////////////////////////////////////////////////////////////////////////////

type ID = number | string

type Document = {
  id: ID
  date: string
  name: string
  signatures: {
    id: ID
    date: string
    investor: {
      _cid: CID
      id: ID
      name: string
    }
    name: string
    signer: {
      _cid: CID
      id: ID
      name: string
    }
    status: string
    status_code: string
    signing_order: boolean
    current_signer: boolean
  }[]
  status: string
  status_code: string
  provider_template_id: string
  voided_at: string
}

///////////////////////////////////////////////////////////////////////////////
// Store
///////////////////////////////////////////////////////////////////////////////

type DocumentMap = Map<CID, Document>

export const useDocumentStore = defineStore('investing/documentStore', () => {
  const route = useRoute()
  const baseUrl = computed(() => `/${route.params.slug}/investing`)
  const v4BaseUrl = computed(() => `/v4/${route.params.slug}/signing`)
  const notificationStore = useNotificationStore()
  const signingSessionToken = ref(null)
  const items = ref<DocumentMap>(new Map())
  const documents = computed(() => Array.from(items.value.keys()).map((key) => items.value.get(key)))

  const fetchDocuments = async (investor_type, investor_id) => {
    clearItems(items)
    const { data, error } = await useFetch(`${baseUrl.value}/${investor_type}/${investor_id}/documents`)
      .get()
      .json<{ data: Document[] }>()

    if (error.value) {
      // TODO: handle error (e.g., display a message to the user)
      console.error(error.value)
      return
    }
    addItems(
      items,
      (data.value?.data ?? []).map((document) => ({ ...document, _cid: document.id })),
    )
  }

  const voidDocument = async (document_id) => {
    try {
      await useFetchv4(`${v4BaseUrl.value}/documents/${document_id}/void`).post({}, { silent: false })
    } catch (error) {
      console.error(error)
    }
  }

  const createDocumentSigningSession = async (document_id: string) => {
    const url = `${v4BaseUrl.value}/documents/signing-session`

    const result = await useFetchv4(url).post({ document_id }, { silent: false })
    signingSessionToken.value = result['id']
  }

  const signatureSendReminder = async (signature_id) => {
    try {
      await useFetchv4(`${v4BaseUrl.value}/signatures/${signature_id}/send-reminder`).post({}, { silent: false })
    } catch (error) {
      console.error(error)
    }
  }

  const addDocument = async (document) => {
    const { data, error } = await useFetch(`${baseUrl.value}/document/add`).post({ document }).json<{ data: string }>()
    if (error.value) {
      // TODO: handle error (e.g., display a message to the user)
      console.error(error.value)
      return
    }
    notificationStore.enqueue('success', data.value.data)
  }

  const downloadDocument = async (document_id) => {
    try {
      const result = await useFetchv4(`${v4BaseUrl.value}/documents/${document_id}/download`).get({ silent: false })
      if (result?.data?.download_url) {
        window.open(result.data.download_url, '_blank')
      }
    } catch (error) {
      console.error(error)
    }
  }

  const pandaDocDownloadDocument = async (document_id) => {
    try {
      const response = await useFetchv4(`${v4BaseUrl.value}/documents/${document_id}/download`).download({
        silent: false,
      })

      await downloadAndSave(response, 'downloaded-file')
      return response
    } catch (error) {
      console.error('Error fetching file from backend:', error)
      throw error
    }
  }

  return {
    items,
    documents,
    signingSessionToken,

    fetchDocuments,
    voidDocument,
    signatureSendReminder,
    addDocument,
    downloadDocument,
    pandaDocDownloadDocument,
    createDocumentSigningSession,
  }
})
